/**
 * /apps/:appId/policies/:policyId — full-page policy editor.
 *
 * Layout:
 *   ← Back to HR Policies
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ [status] [category]                          [Save | Saved]  │
 *   │ Title (large, editable)                                       │
 *   │ Meta row: version · effective from · last updated             │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │                                                                │
 *   │ Big body_md textarea (200 KB room) — most of the page          │
 *   │                                                                │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │ Status workflow buttons (draft → active → retired)             │
 *   │ Delete policy (admin only)                                     │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Members see the same shape but everything is read-only.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ConfirmDeleteModal from '../../components/workspace/ConfirmDeleteModal';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { formatDateTime, formatDate } from '../../lib/format';
import { AttachmentModal } from '../../components/shared/AttachmentComponents';
import { useSpacesUpload, validateFiles } from '../../lib/useSpacesUpload';
import {
  DOCUMENT_ACCEPT, documentAttachment, documentKind, useDocumentCategories,
} from '../../components/workspace/documents/documentHelpers';

/* SVG icons for the toolbar */
const Icon = {
  Bold:       () => <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M15.6 10.79A4.5 4.5 0 0 0 12 3H6v18h7a4.5 4.5 0 0 0 2.6-8.21ZM9 6h3a2 2 0 0 1 0 4H9Zm3.5 12H9v-4h3.5a2 2 0 0 1 0 4Z"/></svg>,
  Italic:     () => <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M10 4v3h2.21l-3.42 10H6v3h8v-3h-2.21l3.42-10H18V4z"/></svg>,
  Underline:  () => <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z"/></svg>,
  BulletList: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z"/></svg>,
  NumList:    () => <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-7v2h14V4H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z"/></svg>,
  Table:      () => <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 2v3H5V5h15zm-9 5h-6v-2h6v2zm0 4h-6v-2h6v2zm0 4h-6v-2h6v2zm9-8h-7v-2h7v2zm0 4h-7v-2h7v2zm0 4h-7v-2h7v2z"/></svg>,
  Quote:      () => <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z"/></svg>,
};

function PolicyEditor({ value, onChange }) {
  const editorRef  = useRef(null);
  const menuRef    = useRef(null);
  const [tableMenu, setTableMenu] = useState(null); // { x, y, td, table }

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, []); // only on mount

  // Close context menu on outside click
  useEffect(() => {
    const close = () => setTableMenu(null);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const exec = (cmd, arg = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange(editorRef.current?.innerHTML || '');
  };

  const insertTable = () => {
    const html = `<table border="1" style="border-collapse:collapse;width:100%">
      <thead><tr><th style="padding:6px 10px;border:1px solid #555">Header 1</th><th style="padding:6px 10px;border:1px solid #555">Header 2</th><th style="padding:6px 10px;border:1px solid #555">Header 3</th></tr></thead>
      <tbody><tr><td style="padding:6px 10px;border:1px solid #555">Cell</td><td style="padding:6px 10px;border:1px solid #555">Cell</td><td style="padding:6px 10px;border:1px solid #555">Cell</td></tr>
      <tr><td style="padding:6px 10px;border:1px solid #555">Cell</td><td style="padding:6px 10px;border:1px solid #555">Cell</td><td style="padding:6px 10px;border:1px solid #555">Cell</td></tr></tbody>
    </table><p><br></p>`;
    editorRef.current?.focus();
    document.execCommand('insertHTML', false, html);
    onChange(editorRef.current?.innerHTML || '');
  };

  // Find ancestor td/th
  const getCell = (node) => {
    let el = node;
    while (el && el !== editorRef.current) {
      if (el.tagName === 'TD' || el.tagName === 'TH') return el;
      el = el.parentElement;
    }
    return null;
  };
  const getTable = (cell) => {
    let el = cell?.parentElement;
    while (el && el !== editorRef.current) {
      if (el.tagName === 'TABLE') return el;
      el = el.parentElement;
    }
    return null;
  };

  const handleContextMenu = (e) => {
    const cell = getCell(e.target);
    if (!cell) return;
    e.preventDefault();
    const table = getTable(cell);
    setTableMenu({ x: e.clientX, y: e.clientY, td: cell, table });
  };

  // Sanitize pasted HTML the same way the Feed composer does, so pasting
  // from Excel/Word/Google Docs (including tables) doesn't drag in external
  // styles/layout — only strips styling, structure (incl. tables) is kept.
  const handlePaste = (e) => {
    e.preventDefault();
    const items = e.clipboardData?.items || [];
    for (const item of items) {
      if (item.kind === 'file') return;
    }
    const html = e.clipboardData.getData('text/html');
    if (html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;

      // Word/Office clipboard HTML carries <style>/<xml>/<meta> blocks and
      // comments — remove them outright, otherwise unwrapping below would
      // dump the raw CSS ("/* Font Definitions */ ...") into the body as text.
      tmp.querySelectorAll('style, script, meta, link, title, xml, head').forEach(el => el.remove());
      const walker = document.createTreeWalker(tmp, NodeFilter.SHOW_COMMENT);
      const comments = [];
      while (walker.nextNode()) comments.push(walker.currentNode);
      comments.forEach(c => c.remove());

      tmp.querySelectorAll('[style]').forEach(el => el.removeAttribute('style'));
      tmp.querySelectorAll('[class]').forEach(el => el.removeAttribute('class'));
      const UNWRAP = ['div', 'span', 'section', 'article', 'aside', 'header', 'footer', 'nav', 'figure', 'figcaption'];
      UNWRAP.forEach(tag => {
        tmp.querySelectorAll(tag).forEach(el => {
          const isBlock = ['div','section','article','aside','header','footer','nav','figure','figcaption'].includes(tag);
          const replacement = document.createDocumentFragment();
          if (isBlock && el.childNodes.length) {
            const p = document.createElement('p');
            while (el.firstChild) p.appendChild(el.firstChild);
            replacement.appendChild(p);
          } else {
            while (el.firstChild) replacement.appendChild(el.firstChild);
          }
          el.parentNode?.replaceChild(replacement, el);
        });
      });
      const ALLOWED_TAGS = new Set(['p','br','b','strong','i','em','u','s','strike','h1','h2','h3','h4','ul','ol','li','a','blockquote','hr','pre','code','table','thead','tbody','tr','th','td']);
      tmp.querySelectorAll('*').forEach(el => {
        if (!ALLOWED_TAGS.has(el.tagName.toLowerCase())) {
          const frag = document.createDocumentFragment();
          while (el.firstChild) frag.appendChild(el.firstChild);
          el.parentNode?.replaceChild(frag, el);
        }
      });

      editorRef.current?.focus();
      document.execCommand('insertHTML', false, tmp.innerHTML);
    } else {
      const text = e.clipboardData.getData('text/plain');
      editorRef.current?.focus();
      document.execCommand('insertText', false, text);
    }
    onChange(editorRef.current?.innerHTML || '');
  };

  const CELL_STYLE = 'padding:6px 10px;border:1px solid #555';

  const addRowBelow = () => {
    const { td, table } = tableMenu;
    const row = td.closest('tr');
    const colCount = table.rows[0].cells.length;
    const newRow = table.insertRow(row.rowIndex + 1);
    for (let i = 0; i < colCount; i++) {
      const cell = newRow.insertCell(i);
      cell.setAttribute('style', CELL_STYLE);
      cell.textContent = 'Cell';
    }
    onChange(editorRef.current?.innerHTML || '');
    setTableMenu(null);
  };

  const addColRight = () => {
    const { td, table } = tableMenu;
    const colIndex = td.cellIndex + 1;
    Array.from(table.rows).forEach(row => {
      const cell = row.insertCell(colIndex);
      cell.setAttribute('style', CELL_STYLE);
      cell.textContent = row.rowIndex === 0 ? 'Header' : 'Cell';
    });
    onChange(editorRef.current?.innerHTML || '');
    setTableMenu(null);
  };

  const deleteRow = () => {
    const { td, table } = tableMenu;
    const row = td.closest('tr');
    if (table.rows.length > 1) table.deleteRow(row.rowIndex);
    onChange(editorRef.current?.innerHTML || '');
    setTableMenu(null);
  };

  const deleteCol = () => {
    const { td, table } = tableMenu;
    const colIndex = td.cellIndex;
    if (table.rows[0].cells.length > 1) {
      Array.from(table.rows).forEach(row => row.deleteCell(colIndex));
    }
    onChange(editorRef.current?.innerHTML || '');
    setTableMenu(null);
  };

  const deleteTable = () => {
    const { table } = tableMenu;
    table.remove();
    onChange(editorRef.current?.innerHTML || '');
    setTableMenu(null);
  };

  const TOOLS = [
    { label: <Icon.Bold />,       title: 'Bold',           cls: 'tb-fmt', action: () => exec('bold') },
    { label: <Icon.Italic />,     title: 'Italic',         cls: 'tb-fmt', action: () => exec('italic') },
    { label: <Icon.Underline />,  title: 'Underline',      cls: 'tb-fmt', action: () => exec('underline') },
    { label: 'H1',                title: 'Heading 1',      cls: 'tb-text', action: () => exec('formatBlock', 'h1') },
    { label: 'H2',                title: 'Heading 2',      cls: 'tb-text', action: () => exec('formatBlock', 'h2') },
    { label: <Icon.Quote />,      title: 'Blockquote',     cls: 'tb-fmt', action: () => exec('formatBlock', 'blockquote') },
    { label: <Icon.BulletList />, title: 'Bullet list',    cls: 'tb-fmt', action: () => exec('insertUnorderedList') },
    { label: <Icon.NumList />,    title: 'Numbered list',  cls: 'tb-fmt', action: () => exec('insertOrderedList') },
    { label: <><Icon.Table /><span style={{fontSize:11,marginLeft:4}}>Table</span></>, title: 'Insert table', cls: 'tb-table', action: insertTable },
  ];

  return (
    <div className="policy-rich-editor">
      <div className="policy-rich-toolbar">
        {TOOLS.map((t, i) => (
          <button key={i} type="button" title={t.title} className={t.cls}
            onMouseDown={e => { e.preventDefault(); t.action(); }}>
            {t.label}
          </button>
        ))}
      </div>
      <div
        ref={editorRef}
        className="policy-rich-body"
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(editorRef.current?.innerHTML || '')}
        onPaste={handlePaste}
        onContextMenu={handleContextMenu}
        data-placeholder="Write your policy content here…"
      />
      {tableMenu && ReactDOM.createPortal(
        <div ref={menuRef} className="table-ctx-menu" style={{ top: tableMenu.y, left: tableMenu.x }}
          onMouseDown={e => e.stopPropagation()}>
          <button onClick={addRowBelow}>Add row below</button>
          <button onClick={addColRight}>Add column right</button>
          <hr />
          <button onClick={deleteRow}>Delete row</button>
          <button onClick={deleteCol}>Delete column</button>
          <hr />
          <button className="danger" onClick={deleteTable}>Delete table</button>
        </div>,
        document.body
      )}
    </div>
  );
}

const STATUS_OPTIONS = [
  { value: 'draft',   label: 'Draft' },
  { value: 'active',  label: 'Active' },
  { value: 'retired', label: 'Retired' },
];

export default function PolicyDetail() {
  const { appId, policyId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { canWrite } = useWorkspace();
  const { categories } = useDocumentCategories(appId);
  const { uploadFiles, uploading } = useSpacesUpload(appId, 'hr_document');
  const [viewFile, setViewFile]     = useState(false);

  const [policy, setPolicy]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [savedAt, setSavedAt]       = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Local editable buffers
  const [title, setTitle]                 = useState('');
  const [categoryId, setCategoryId]       = useState('');
  const [body, setBody]                   = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [version, setVersion]             = useState(1);
  const [status, setStatus]               = useState('draft');
  const [saving, setSaving]               = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = await api.policies.get(appId, policyId);
      setPolicy(p);
      setTitle(p.title);
      setCategoryId(p.category_id ? String(p.category_id) : '');
      setBody(p.body_md || '');
      setEffectiveFrom(p.effective_from || '');
      setVersion(p.version || 1);
      setStatus(p.status || 'draft');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, appId, policyId]);

  useEffect(() => { load(); }, [load]);

  const dirty = policy && (
    title !== policy.title ||
    categoryId !== (policy.category_id ? String(policy.category_id) : '') ||
    (body || '') !== (policy.body_md || '') ||
    (effectiveFrom || '') !== (policy.effective_from || '') ||
    Number(version) !== Number(policy.version || 1) ||
    status !== policy.status
  );

  const handleSave = async () => {
    setError(null);
    if (!title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    try {
      const updated = await api.policies.update(appId, policyId, {
        title: title.trim(),
        ...(categoryId ? { category_id: Number(categoryId) } : {}),
        body_md: body || null,
        effective_from: effectiveFrom || null,
        version: Number(version) || 1,
        status,
      });
      setPolicy(updated);
      setSavedAt(new Date());
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // File documents: swap in a new upload (saved immediately)
  const handleReplaceFile = async (f) => {
    if (!f) return;
    setError(null);
    const { errors } = validateFiles([f]);
    if (errors.length) { setError(errors[0]); return; }
    try {
      const [up] = await uploadFiles([f]);
      const updated = await api.policies.update(appId, policyId, {
        file_url: up.file_url, file_key: up.file_key, file_name: up.file_name,
        content_type: up.content_type, file_size: up.file_size,
      });
      setPolicy(updated);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    }
  };

  // Quick status workflow buttons (independent of the main Save flow).
  const handleSetStatus = async (newStatus) => {
    setError(null);
    try {
      const updated = await api.policies.update(appId, policyId, { status: newStatus });
      setPolicy(updated);
      setStatus(updated.status);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  if (loading && !policy) return <p className="muted">Loading document…</p>;
  if (error && !policy)   return <div className="banner banner-error">{error}</div>;
  if (!policy)            return null;

  return (
    <div className="policy-detail">
      <div className="role-detail-breadcrumb">
        <Link className="btn-back" to={`/apps/${appId}/policies`}>←</Link>
      </div>

      <header className="policy-detail-header">
        <div className="policy-detail-pills">
          <span className={`status-pill status-${policy.status}`}>{policy.status}</span>
          {categories.find(c => String(c.id) === String(policy.category_id))?.name && <span className="policy-category">{categories.find(c => String(c.id) === String(policy.category_id))?.name}</span>}
          <span className="muted small">{documentKind(policy)} · v{policy.version}</span>
        </div>
        {canWrite ? (
          <input
            className="form-input policy-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="Document title"
          />
        ) : (
          <h1 className="page-title">{policy.title}</h1>
        )}
        {canWrite && (
          <div className="policy-save-state">
            <button
              className="btn-primary"
              onClick={handleSave}
              disabled={saving || !dirty}
            >
              {saving ? 'Saving…' : dirty ? 'Save Changes' : 'Saved'}
            </button>
            {savedAt && !dirty && (
              <span className="muted small">
                Saved at {savedAt.toLocaleTimeString()}
              </span>
            )}
          </div>
        )}
      </header>

      {error && <div className="banner banner-error">{error}</div>}

      {/* Meta row */}
      <div className="policy-meta-grid">
        <label className="policy-meta-field">
          <span>Status</span>
          {canWrite ? (
            <select
              className="form-input"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ) : (
            <span>{STATUS_OPTIONS.find(o => o.value === status)?.label || status}</span>
          )}
        </label>
        <label className="policy-meta-field">
          <span>Category</span>
          {canWrite ? (
            <select className="form-input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              {!categoryId && <option value="">— choose —</option>}
              {categories.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </select>
          ) : (
            <span>{categories.find(c => String(c.id) === String(policy.category_id))?.name || <span className="muted">—</span>}</span>
          )}
        </label>
        <label className="policy-meta-field">
          <span>Effective from</span>
          {canWrite ? (
            <input
              type="date"
              className="form-input"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
          ) : (
            <span>{policy.effective_from ? formatDate(policy.effective_from) : <span className="muted">—</span>}</span>
          )}
        </label>
        <label className="policy-meta-field">
          <span>Version</span>
          {canWrite ? (
            <input
              type="number"
              className="form-input"
              min={1}
              max={9999}
              value={version}
              onChange={(e) => setVersion(e.target.value)}
            />
          ) : (
            <span>v{policy.version}</span>
          )}
        </label>
        <div className="policy-meta-field">
          <span>Last updated</span>
          <span className="muted">{formatDateTime(policy.updated_at)}</span>
        </div>
      </div>

      {/* Uploaded file (file documents) */}
      {policy.doc_type === 'file' && (
        <section className="policy-body-section">
          <div className="policy-body-head"><h2>File</h2></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, wordBreak: 'break-all' }}>{policy.file_name || 'File'}</span>
            {policy.file_size ? <span className="muted small">{(policy.file_size / 1024 / 1024).toFixed(2)} MB</span> : null}
            {policy.file_url && <button type="button" className="btn-secondary" onClick={() => setViewFile(true)}>Open</button>}
            {canWrite && (
              <label className="btn-secondary" style={{ cursor: uploading ? 'wait' : 'pointer' }}>
                {uploading ? 'Uploading…' : 'Replace file'}
                <input type="file" accept={DOCUMENT_ACCEPT} style={{ display: 'none' }} disabled={uploading}
                  onChange={e => { handleReplaceFile(e.target.files?.[0]); e.target.value = ''; }} />
              </label>
            )}
          </div>
        </section>
      )}
      {viewFile && <AttachmentModal attachment={documentAttachment(policy)} onClose={() => setViewFile(false)} />}

      {/* Body editor — the big one (written documents) */}
      {policy.doc_type !== 'file' && (
      <section className="policy-body-section">
        <div className="policy-body-head">
          <h2>Document text</h2>
          <span className="muted small">{body.length.toLocaleString()} / 200,000 characters</span>
        </div>
        {canWrite ? (
          <PolicyEditor value={body} onChange={setBody} />
        ) : body ? (
          <div className="policy-body-readonly" dangerouslySetInnerHTML={{ __html: body }} />
        ) : (
          <p className="muted">No content yet.</p>
        )}
      </section>
      )}

      {/* Status workflow shortcuts */}
      {canWrite && (
        <div className="policy-workflow">
          <span className="muted small">Quick actions:</span>
          {policy.status !== 'active' && (
            <button className="btn-link-sm" onClick={() => handleSetStatus('active')}>
              Mark Active
            </button>
          )}
          {policy.status !== 'draft' && (
            <button className="btn-link-sm" onClick={() => handleSetStatus('draft')}>
              Move to Draft
            </button>
          )}
          {policy.status !== 'retired' && (
            <button className="btn-link-sm" onClick={() => handleSetStatus('retired')}>
              Retire
            </button>
          )}
        </div>
      )}

      {canWrite && (
        <div className="task-detail-danger">
          <button className="btn-link-sm danger" onClick={() => setDeleteOpen(true)}>
            Delete this document
          </button>
        </div>
      )}

      <ConfirmDeleteModal
        open={deleteOpen}
        title={`Delete "${policy.title}"?`}
        message="The document will be permanently removed."
        onConfirm={async () => {
          await api.policies.remove(appId, policyId);
          navigate(`/apps/${appId}/policies`);
        }}
        onClose={() => setDeleteOpen(false)}
      />
    </div>
  );
}
